import { useDispatch } from "react-redux";
import { useRef, useCallback } from "react";
import {
    useExportReportMutation,
    useLazyGetExportStatusQuery,
} from "../../../slices/exportReportSlice";
import {
    setShowExportToast,
    setHighVolumeGeneratingReport,
    addExportJob,
    updateExportJob,
    removeExportJob,
} from "../../../slices/highVolumeSlice";

import {
    triggerDownload,
    getAdjustedEffectiveEndDateForApi,
} from "../../../utils/helpers";
import { HIGH_VOLUME_MESSAGES, LEAVE_SCHEDULE_TYPE } from "../../../constants";

const EXPORT_SCOPE = "Personal";
const EXPORT_TYPE = "ReportingCapabilitiesExport";
const MAX_POLL_RETRIES = 50;
const DEFAULT_FILE_NAME = "Report.xlsx";

/* -------------------------------------------------------------------------- */
/*                               HELPER UTILS                                 */
/* -------------------------------------------------------------------------- */

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const showToast = (dispatch) => {
    dispatch(setShowExportToast(true));
};

// Normalizes non-ASCII characters (e.g. \u001a from upstream org-access data) to API-safe ASCII
const sanitizeForApi = (str) => {
    if (typeof str !== "string") return str;
    return str
        .replaceAll(/([A-Za-z])[\u0000-\u001F]([A-Za-z])/g, "$1'$2")
        .replaceAll(/\s[\u0000-\u001F]\s/g, " - ")
        .replaceAll(/[\u2010-\u2015\u2212]/g, "-")
        .replaceAll(/[\u2018\u2019\u201A]/g, "'")
        .replaceAll(/[\u201C\u201D\u201E]/g, '"')
        .replaceAll("\u2026", "...")
        .replaceAll(/[^\x20-\x7E©]/g, "");
};

/* -------------------------------------------------------------------------- */
/*                        REQUEST PARAM BUILDER (OPTIMIZED)                    */
/* -------------------------------------------------------------------------- */

const filterPolicyDivisions = (policyAndDivisions = []) =>
    policyAndDivisions.filter((item) => item.includes("-"));

const getLeaveScheduleTypes = (leaveScheduleTypes, getOptions) => {
    if (leaveScheduleTypes === undefined) {
        const options = getOptions(LEAVE_SCHEDULE_TYPE);
        if (Array.isArray(options)) {
            return options.map((opt) => opt.value);
        }
        return options?.value ? [options.value] : [];
    }
    if (Array.isArray(leaveScheduleTypes)) {
        return leaveScheduleTypes.map((item) => item.value);
    }
    return [leaveScheduleTypes];
};

const baseParams = (rest, selectedIsfOrganizationIds) => {
    const params = {
        ...rest,
        isfOrganizationIds: selectedIsfOrganizationIds,
    };
    return Object.fromEntries(
        Object.entries(params).filter(([_, value]) => {
            if (Array.isArray(value)) return value.length > 0;
            if (typeof value === "string") return value.trim().length > 0;
            return value !== undefined && value !== null;
        })
    );
};

/* -------------------------------------------------------------------------- */
/*                BUILD EXPORT REQUEST PARAMETERS (ORIGINAL)                  */
/* -------------------------------------------------------------------------- */

export const buildExportRequestParameters = ({
    selectedIsfOrganizationIds,
    dataViewType,
    saveUpdateReportParameters = {},
    filterStatusValues = [],
    filterApplyingReportingTypeValues,
    filterProtectionStatusValues = [],
    filterAbsenceClaimStatusValues = [],
    filterAbsenceLeaveStatusValues = [],
    filterAbsencePaymentStatusValues = [],
    filteredPaymentsStatusValues = [],
    selectedReportingGroup = [],
    getOptions,
    subviewMap,
    getCommonDateFilters,
    subviewName,
}) => {
    const {
        coverageTypes = [],
        reportingPeriod,
        statuses,
        policyAndDivisions = [],
        planRequestStatus,
        protectionStatuses,
        effectiveEndDt,
        effectiveStartDt,
        leaveScheduleTypes,
        ...rest
    } = saveUpdateReportParameters;

    const getDates = (dateType) => {
        const dateFilters = getCommonDateFilters(dateType);
        return {
            effectiveStartDt: dateFilters.effectiveStartDate,
            effectiveEndDt: getAdjustedEffectiveEndDateForApi({
                effectiveEndDate: dateFilters.effectiveEndDate,
                subviewName,
            }),
        };
    };

    switch (dataViewType) {
        case "Disability":
            return baseParams(
                {
                    ...rest,
                    claimCoverageTypes: coverageTypes,
                    claimStatus: filterStatusValues,
                    policyAndDivisions:
                        filterPolicyDivisions(policyAndDivisions),
                    reportingPeriodDateTypeDisability:
                        filterApplyingReportingTypeValues,
                    reportingPeriod:
                        reportingPeriod === "" ? "All Open" : reportingPeriod,
                    ...getDates("claims"),
                },
                selectedIsfOrganizationIds
            );

        case "Leave":
            return baseParams(
                {
                    ...rest,
                    leaveCoverageType: coverageTypes[0],
                    leaveStatus: filterStatusValues,
                    periodStatuses: planRequestStatus,
                    protectionStatuses: filterProtectionStatusValues,
                    reportingPeriodDateTypeLeave:
                        filterApplyingReportingTypeValues,
                    reportingPeriod:
                        reportingPeriod === "" ? "All Open" : reportingPeriod,
                    ...getDates(),
                    leaveScheduleTypes: getLeaveScheduleTypes(
                        leaveScheduleTypes,
                        getOptions
                    ),
                },
                selectedIsfOrganizationIds
            );

        case "PaymentHistory":
            return baseParams(
                {
                    ...rest,
                    claimCoverageTypes: coverageTypes,
                    paymentStatuses: filteredPaymentsStatusValues,
                    policyAndDivisions:
                        filterPolicyDivisions(policyAndDivisions),
                    reportingPeriod:
                        reportingPeriod === "" ? "All Open" : reportingPeriod,
                    ...getDates(),
                },
                selectedIsfOrganizationIds
            );

        case "Absence":
            return baseParams(
                {
                    ...rest,
                    claimCoverageTypes: coverageTypes.filter(
                        (type) => type !== "Leave"
                    ),
                    claimStatus: filterAbsenceClaimStatusValues,
                    leaveCoverageType: coverageTypes[0],
                    protectionStatuses: filterProtectionStatusValues,
                    periodStatuses: planRequestStatus,
                    leaveStatus: filterAbsenceLeaveStatusValues,
                    policyAndDivisions:
                        filterPolicyDivisions(policyAndDivisions),
                    reportingPeriodDateTypeAbsence:
                        filterApplyingReportingTypeValues,
                    reportingPeriod:
                        reportingPeriod === "" ? "All Open" : reportingPeriod,
                    ...getDates("claims"),
                    leaveScheduleTypes: getLeaveScheduleTypes(
                        leaveScheduleTypes,
                        getOptions
                    ),
                },
                selectedIsfOrganizationIds
            );

        case "Life":
            return baseParams(
                {
                    ...rest,
                    paymentStatuses:
                        subviewMap === "Payments"
                            ? filteredPaymentsStatusValues
                            : [...new Set(filterAbsencePaymentStatusValues)],
                    claimStatus:
                        subviewMap === "Claims"
                            ? filterStatusValues
                            : filterAbsenceClaimStatusValues,
                    claimCoverageTypes: coverageTypes,
                    policyAndDivisions:
                        filterPolicyDivisions(policyAndDivisions),
                    ...getDates(),
                    reportingPeriod,
                },
                selectedIsfOrganizationIds
            );

        default:
            return {};
    }
};

/* -------------------------------------------------------------------------- */
/*                             EXPORT EXECUTION                                */
/* -------------------------------------------------------------------------- */

const executeExport = async ({
    exportReportMutation,
    requestParameters,
    selectedReportViewId,
    userId,
    selectedIsfOrganizationIds,
    displayMetadata,
}) => {
    try {
        const response = await exportReportMutation({
            dataToUpdate: {
                viewId: selectedReportViewId,
                isfUserId: userId,
                isfOrganizationIds: selectedIsfOrganizationIds,
                exportScope: EXPORT_SCOPE,
                exportType: EXPORT_TYPE,
                requestParameters,
                displayMetadata,
            },
        }).unwrap();

        return {
            success: true,
            jobId: response?.jobId || response?.id,
            data: response,
        };
    } catch (error) {
        console.error("Export initiation failed:", error);
        return { success: false, error };
    }
};

/* -------------------------------------------------------------------------- */
/*                            DOWNLOAD QUEUE HELPERS                           */
/* -------------------------------------------------------------------------- */

const createDownloadQueue = (downloadQueueRef, isDownloadingRef) => {
    const processQueue = () => {
        if (isDownloadingRef.current) return;

        const nextDownload = downloadQueueRef.current.shift();
        if (!nextDownload) return;

        isDownloadingRef.current = true;
        triggerDownload(nextDownload.url, nextDownload.fileName);

        setTimeout(() => {
            isDownloadingRef.current = false;
            processQueue();
        }, 800);
    };

    const enqueue = (url, fileName) => {
        if (!url) return;
        downloadQueueRef.current.push({ url, fileName });
        processQueue();
    };

    return { enqueue };
};

/* -------------------------------------------------------------------------- */
/*                               STATUS POLLING                                */
/* -------------------------------------------------------------------------- */

const processExportStatusResponse = ({
    result,
    dispatch,
    downloadedJobsRef,
    enqueue,
}) => {
    if (!Array.isArray(result)) return;

    result.forEach((apiJob) => {
        const fileName = `${apiJob.reportName}.xlsx` || DEFAULT_FILE_NAME;
        const isSuccess = apiJob.status === "Completed";
        const isFailed = apiJob.status === "Failed";
        const isInProgress = apiJob.status === "InProgress";

        // Update Redux with the job status
        dispatch(
            updateExportJob({
                jobId: apiJob.jobId,
                status: isSuccess
                    ? "success"
                    : isFailed
                        ? "failed"
                        : "progress",
                downloadUrl: apiJob.downloadUrl,
                fileName,
                percentComplete: apiJob.percentComplete,
                message: apiJob.message,
            })
        );

        // Show toast and trigger download for ANY completed/failed job
        if (!isInProgress) {
            showToast(dispatch);

            // Download if completed successfully and not already downloaded
            if (
                isSuccess &&
                apiJob.downloadUrl &&
                !downloadedJobsRef.current.has(apiJob.jobId)
            ) {
                downloadedJobsRef.current.add(apiJob.jobId);
                enqueue(apiJob.downloadUrl, fileName);
            }
        }

        // Remove completed/failed job from Redux after a delay
        if (!isInProgress) {
            setTimeout(() => {
                dispatch(removeExportJob(apiJob.jobId));
            }, 10000);
        }
    });
};

/* -------------------------------------------------------------------------- */
/*                                 MAIN HOOK                                   */
/* -------------------------------------------------------------------------- */

export const useHighVolumeExport = () => {
    const dispatch = useDispatch();
    const [exportReportMutation] = useExportReportMutation();
    const [getExportStatus] = useLazyGetExportStatusQuery();

    const abortControllersRef = useRef(new Map());
    const downloadedJobsRef = useRef(new Set());
    const downloadQueueRef = useRef([]);
    const isDownloadingRef = useRef(false);
    const pollingIntervalRef = useRef(null);
    const activeJobsRef = useRef(new Set());
    const jobResolversRef = useRef(new Map());
    const userEmailRef = useRef(null);

    const { enqueue } = createDownloadQueue(downloadQueueRef, isDownloadingRef);

    // Single shared polling loop
    const startPolling = useCallback(() => {
        if (pollingIntervalRef.current) return; // Already polling

        pollingIntervalRef.current = setInterval(async () => {
            if (activeJobsRef.current.size === 0) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
                return;
            }

            try {
                const result = await getExportStatus({
                    email: userEmailRef.current,
                }).unwrap();

                processExportStatusResponse({
                    result,
                    dispatch,
                    downloadedJobsRef,
                    enqueue,
                });

                // Check and resolve any completed jobs
                if (Array.isArray(result)) {
                    result.forEach((apiJob) => {
                        if (
                            apiJob.status !== "InProgress" &&
                            activeJobsRef.current.has(apiJob.jobId)
                        ) {
                            const resolver = jobResolversRef.current.get(
                                apiJob.jobId
                            );
                            if (resolver) {
                                resolver(apiJob);
                                jobResolversRef.current.delete(apiJob.jobId);
                                activeJobsRef.current.delete(apiJob.jobId);
                            }
                        }
                    });
                }
            } catch (error) {
                console.error("Polling error:", error);
            }
        }, 15000);
    }, [dispatch, getExportStatus, enqueue]);

    const cancelExport = useCallback(
        (jobId) => {
            const controller = abortControllersRef.current.get(jobId);
            controller?.abort();
            abortControllersRef.current.delete(jobId);

            // Remove cancelled job from Redux
            if (jobId) {
                dispatch(removeExportJob(jobId));
            }

            // Check if any jobs are still in progress
            dispatch((dispatch, getState) => {
                const currentJobs = getState().highVolume?.exportJobs || [];
                const remainingInProgressJobs = currentJobs.filter(
                    (job) => job.status === "progress"
                );

                if (remainingInProgressJobs.length === 0) {
                    dispatch(setHighVolumeGeneratingReport(false));
                }
            });

            showToast(dispatch);
        },
        [dispatch]
    );

    /* ------------------------------ EXPORT --------------------------------- */

    const handleExport = async (params) => {
        const {
            dropdownSelectedReport,
            selectedIsfOrganizationIds,
            dataViewType,
            selectedReportViewId,
            userDetails,
            setHighVolumeCardMessage,
            setFiltersVisible,
            setDropdownSelectedReport,
            saveUpdateReportParameters,
            filterStatusValues,
            filterApplyingReportingTypeValues,
            filterProtectionStatusValues,
            filterAbsenceClaimStatusValues,
            filterAbsenceLeaveStatusValues,
            filterAbsencePaymentStatusValues,
            filteredPaymentsStatusValues,
            selectedReportingGroup,
            selectedOrganizations,
            getOptions,
            enableReportGroupFilter,
            subviewMap,
            selectedRelatedOrgs,
            getCommonDateFilters,
        } = params;

        const fileName = `${dropdownSelectedReport.label}.xlsx`;

        // Generate temporary jobId for immediate display
        const tempJobId = `temp-${Date.now()}`;

        dispatch(setHighVolumeGeneratingReport(true));

        // Add job to Redux immediately with temporary ID
        dispatch(
            addExportJob({
                jobId: tempJobId,
                fileName,
                status: "progress",
            })
        );

        // Show toast immediately
        showToast(dispatch);

        setHighVolumeCardMessage(HIGH_VOLUME_MESSAGES.REPORT_GENERATED);
        setFiltersVisible(false);
        setDropdownSelectedReport(null);

        const requestParameters = buildExportRequestParameters({
            selectedIsfOrganizationIds,
            dataViewType,
            saveUpdateReportParameters,
            filterStatusValues,
            filterApplyingReportingTypeValues,
            filterProtectionStatusValues,
            filterAbsenceClaimStatusValues,
            filterAbsenceLeaveStatusValues,
            filterAbsencePaymentStatusValues,
            filteredPaymentsStatusValues,
            selectedReportingGroup,
            getOptions,
            subviewMap,
            getCommonDateFilters,
            subviewName: dropdownSelectedReport?.subViewName,
        });

        const reportGroup = selectedReportingGroup.map((group) => ({
            ReportGroupName: sanitizeForApi(group.name),
            ReportGroupNumber: group.value.split(".")[0] ?? group.value,
        }));

        const organizationName =
            selectedOrganizations.related.length > 0
                ? selectedOrganizations.organization.ORGANIZATIONGROUPNAME
                : selectedOrganizations.organization.ORGNIZATIONNAME;

        const sanitizedRelatedOrgs = selectedRelatedOrgs.map((org) => ({
            ...org,
            OrganizationName: sanitizeForApi(org.OrganizationName),
        }));

        const displayMetadata = {
            ...(enableReportGroupFilter
                ? { ReportingGroups: reportGroup }
                : {}),
            OrganizationName: sanitizeForApi(organizationName),
            ...(sanitizedRelatedOrgs.length > 0
                ? { RelatedOrganizations: sanitizedRelatedOrgs }
                : {}),
            IsInternalUser: userDetails?.isUserInternal,
        };

        const result = await executeExport({
            exportReportMutation,
            requestParameters,
            selectedReportViewId,
            userId: userDetails?.userId,
            selectedIsfOrganizationIds,
            displayMetadata,
        });

        if (!result.success || !result.jobId) {
            dispatch(setHighVolumeGeneratingReport(false));
            // Update temp job to failed status
            dispatch(
                updateExportJob({
                    jobId: tempJobId,
                    status: "failed",
                })
            );
            // Remove failed job from Redux after a delay
            setTimeout(() => {
                dispatch(removeExportJob(tempJobId));
            }, 10000);
            return result;
        }

        // Update temp job with actual jobId
        dispatch(removeExportJob(tempJobId));
        dispatch(
            addExportJob({
                jobId: result.jobId,
                fileName,
                status: "progress",
            })
        );

        // Register job for shared polling
        userEmailRef.current = userDetails?.userEmail?.toUpperCase();
        activeJobsRef.current.add(result.jobId);

        // Start shared polling if not already running
        startPolling();

        // Wait for job completion via promise
        result.status = await new Promise((resolve) => {
            jobResolversRef.current.set(result.jobId, resolve);

            // Timeout after max retries
            setTimeout(() => {
                if (jobResolversRef.current.has(result.jobId)) {
                    jobResolversRef.current.delete(result.jobId);
                    activeJobsRef.current.delete(result.jobId);
                    resolve({ status: "Timeout", jobId: result.jobId });
                }
            }, MAX_POLL_RETRIES * 15000);
        });

        // Handle failed/timeout/error status
        if (
            result.status?.status === "Error" ||
            result.status?.status === "Timeout"
        ) {
            dispatch(
                updateExportJob({
                    jobId: result.jobId,
                    status: "failed",
                })
            );
            showToast(dispatch);
            setTimeout(() => {
                dispatch(removeExportJob(result.jobId));
            }, 10000);
        }

        dispatch((dispatch, getState) => {
            const currentJobs = getState().highVolume?.exportJobs || [];
            const remainingInProgressJobs = currentJobs.filter(
                (job) => job.status === "progress"
            );

            if (remainingInProgressJobs.length === 0) {
                dispatch(setHighVolumeGeneratingReport(false));
            }
        });

        return result;
    };

    return {
        handleExport,
        cancelExport,
    };
};
